import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Icons,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import { createStyles } from './PostCard.styles';
import type { PostCardProps } from './types';

const PostCard = ({
  author,
  timestamp,
  edited = false,
  content,
  media,
  tags,
  reactions,
  comments,
  postActions,
  ariaLabel = 'Post',
}: PostCardProps) => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const initials = author.initials || author.name.slice(0, 2).toUpperCase();

  const renderSection = (label: string, children: React.ReactNode) => {
    if (!children) return null;

    return (
      <>
        <Box component="hr" sx={styles.divider} />
        <Box component="section" aria-label={label} sx={styles.section}>
          {children}
        </Box>
      </>
    );
  };

  return (
    <Box component="article" aria-label={ariaLabel} sx={styles.card}>
      <Box component="header" sx={styles.header}>
        <Box sx={styles.author}>
          <Avatar
            altText={author.name}
            fallbackText={initials}
            kind="circular"
            src={author.avatarUrl}
            sx={styles.avatar}
          />
          <Box sx={styles.authorDetails}>
            <Box sx={styles.authorNameRow}>
              <Typography sx={styles.authorName} title={author.name}>
                {author.name}
              </Typography>
              {author.role ? (
                <Chip label={author.role} size="small" sx={styles.roleBadge} />
              ) : null}
            </Box>
            <Typography sx={styles.timestamp}>{timestamp}</Typography>
          </Box>
        </Box>

        {postActions || (
          <IconButton
            aria-label="Post options"
            size="small"
            sx={styles.options}
          >
            <Icons.MoreVert />
          </IconButton>
        )}
      </Box>

      {content ? <Box sx={styles.content}>{content}</Box> : null}
      {media ? <Box sx={styles.media}>{media}</Box> : null}
      {edited ? <Typography sx={styles.edited}>(Edited)</Typography> : null}
      {renderSection('Post tags', tags)}
      {renderSection('Post reactions', reactions)}
      {renderSection('Post comments and replies', comments)}
    </Box>
  );
};

export default PostCard;
